import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Injector,
    Input,
    OnDestroy,
    OnInit
} from '@angular/core';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { EditPanelComponent } from '@Work/Accounts/proprietary/controls/form-controls/edit-panel/edit-panel.component';
import { DocumentDistributeObjectService } from '@Core/models/proprietary/document-distribute/document-distribute-object.service';
import { TaskObjectState } from '@Core/models/proprietary/task/task-object.state';

@Component({
    standalone: false,
    selector: 'ep-document-distribute-edit-panel',
    templateUrl: './document-distribute-edit-panel.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentDistributeEditPanelComponent extends EditPanelComponent implements OnInit, OnDestroy {
    @Input()
    set document(value: any) {
        this.setFormData(value);
    }
    @Input()
    documentCount;

    allowedMethods: any = {};

    distributeOptions: any = [];

    constructor(
        private injector: Injector,
        private cdRef: ChangeDetectorRef,
        public documentDistributeOS: DocumentDistributeObjectService
    ) {
        super(injector);

        this.form = new UntypedFormGroup(
            {
                Document: this.fb.group({
                    Doc_ID: [''],
                    Doc_Name: ['', Validators.required],
                    Doc_Desc: [''],
                    Doc_Class: [''],
                    Dist_Method: [''],
                    Base_Doc_Type: ['']
                })
            },
            { updateOn: 'blur' }
        );
    }

    ngOnInit() {
        this.ignorePageState = true;

        super.ngOnInit();
        this.formChangeSub = this.form.valueChanges.subscribe((value) => {
            this.documentDistributeOS.updateDocument(value.Document);
        });

        this.setAllowedMethods();
    }

    applyRules() { }

    setFormData(value: any) {
        if (value) {
            this.form.controls.Document.patchValue(value, { emitEvent: false });
            this.cdRef.detectChanges();
            this.applyRules();
        }
    }

    setAllowedMethods() {
        let options =
            this.documentCount === 1
                ? {
                    codeValues: [
                        { Code: 'Q', Code_Desc: 'Queue Documents' },
                        { Code: 'C', Code_Desc: 'Quick Correspond' },
                        { Code: 'P', Code_Desc: 'Quick Print' },
                        { Code: 'F', Code_Desc: 'Quick File' }
                    ]
                }
                : {
                    codeValues: [
                        { Code: 'Q', Code_Desc: 'Queue Documents' },
                        { Code: 'F', Code_Desc: 'Quick File' }
                    ]
                };

        let currentTask = this.store.selectSnapshot(TaskObjectState.task);
        if (currentTask?.Workflow_Step) {
            let taskDistributeOptions = {
                Default_Dist_Method: currentTask?.Workflow_Step.Distribution_Method,
                Queue_Documents: currentTask?.Workflow_Step.Allow_Queue_Ind,
                Quick_Correspond: currentTask?.Workflow_Step.Allow_QuickCorrespond_Ind,
                Quick_Print: currentTask?.Workflow_Step.Allow_QuickPrint_Ind,
                Quick_File: currentTask?.Workflow_Step.Allow_QuickFile_Ind
            };

            this.distributeOptions = options.codeValues.filter((codeValue) => {
                switch (codeValue.Code) {
                    case 'Q':
                        return taskDistributeOptions.Queue_Documents === 'Y';
                    case 'C':
                        return taskDistributeOptions.Quick_Correspond === 'Y';
                    case 'P':
                        return taskDistributeOptions.Quick_Print === 'Y';
                    case 'F':
                        return taskDistributeOptions.Quick_File === 'Y';
                    default:
                        return false; // Excludes other codes by default
                }
            });

            if (this.distributeOptions.some((codeValue) => codeValue.Code === taskDistributeOptions.Default_Dist_Method)) {
                let document = this.form.getRawValue().Document;
                document.Dist_Method = taskDistributeOptions.Default_Dist_Method;
                this.form.controls.Document.patchValue(document, { emitEvent: true });
                this.cdRef.detectChanges();
            } else if (!taskDistributeOptions.Default_Dist_Method) {
                let document = this.form.getRawValue().Document;
                if (!this.distributeOptions.some((codeValue) => codeValue.Code === document.Dist_Method)) {
                    // If default Dist_Method value from the document is not in the available distribution options, set it to null
                    document.Dist_Method = null;
                    this.form.controls.Document.patchValue(document, { emitEvent: true });
                    this.cdRef.detectChanges();
                }
            }
        } else {
            this.distributeOptions = options.codeValues;
        }
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }
}
